import React, { useMemo } from 'react';
import _get from 'lodash/get';
import cn from 'classnames';
import styles from './index.module.scss';

import Tab from './Tab';
import { TabsContext } from './context';
import { UiKit } from 'components';

/**
 * @param {Object} props
 * @param {import('utils/types').Size} props.size
 * @param {boolean} props.rounded - Whether the pill switch is rounded.
 * @param {string|null} props.value - The current value of the pill switch.
 * @param {boolean} props.disabled - Whether the pill switch is disabled.
 * @param {string} props.className - Additional class names for the pill switch.
 * @param {string} props.tabClassName - Class names for the tab elements.
 * @param {string} props.activeTabClassName - Class names for the active tab element.
 * @param {string} props.floatingPillClassName - Class names for the floating pill element.
 * @param {function} props.onChange - Callback function when the value changes.
 * @param {ReactNode} props.children - Child elements to render inside the pill switch.
 */
function Tabs({
  size = 'md',
  rounded = false,
  value = null,
  disabled = false,
  className = '',
  tabClassName = '',
  activeTabClassName = '',
  floatingPillClassName = '',
  onChange = () => {},
  children = null
}) {
  const { activeIndex, count } = useMemo(() => {
    const childElements = children || [];
    const indexMap = childElements.reduce((acc, child, index) => {
      const pillValue = _get(child, 'props.value');
      acc[pillValue] = index;
      return acc;
    }, {});
    return {
      activeIndex: _get(indexMap, value, -1),
      count: childElements.length
    };
  }, [value, children]);

  return (
    <TabsContext.Provider
      value={{
        activeValue: value,
        isDisabled: disabled,
        tabClassName,
        activeTabClassName,
        onChange
      }}
    >
      <div
        data-size={size}
        data-rounded={rounded}
        className={cn(styles.container, className)}
      >
        <UiKit.Grid
          gap="8"
          templateColumns={`repeat(${count}, 1fr)`}
          className={styles.content}
        >
          <div
            key="active-index-bg"
            style={{
              '--uikit-tab-index': activeIndex,
              '--uikit-tab-count': count,
              transform:
                activeIndex >= 0
                  ? `translateX(calc(100% * ${activeIndex} + var(--gap) * ${activeIndex}))`
                  : 'translateX(0)'
            }}
            className={cn(styles.floatingPill, floatingPillClassName)}
          />
          {children}
        </UiKit.Grid>
      </div>
    </TabsContext.Provider>
  );
}

Tabs.Tab = Tab;

export default Tabs;
